import { MapperNodeDataSchema } from '@cotera/api';
import type { WorkflowNodeData } from '../types';
import type {
  WorkflowNodeHydration,
  WorkflowNodeUpstreamRef,
} from '../workflow-node-base';
import type { WorkflowNodeOutputShapeContext } from '../workflow-node-output-shape';
import { deriveMapperOutputShape } from './mapper-fields';
import { TypedWorkflowNode } from './typed-workflow-node';

/**
 * Reshapes the run's data for the steps below it: every field it publishes is
 * pulled from ANY upstream output — the trigger payload, a direct predecessor,
 * an ancestor several levels back — into one flat record.
 *
 * It exists for the nodes that can only read their direct input. An agent node
 * is handed its predecessor's output as its prompt and has nowhere to declare a
 * ref, so an agent four steps down could not see the trigger payload at all.
 * Putting a mapper directly above it turns "reach back up the graph" into
 * "read your input", which is the one thing every node can already do.
 *
 * With nothing mapped it passes its input through unchanged, so an
 * unconfigured mapper is a no-op rather than a step that blanks the branch —
 * and its output shape then mirrors its predecessor's.
 */
export class MapperWorkflowNode extends TypedWorkflowNode<
  typeof MapperNodeDataSchema
> {
  protected readonly schema = MapperNodeDataSchema;

  static override create(
    data: WorkflowNodeData,
    hydration: WorkflowNodeHydration
  ): MapperWorkflowNode {
    return new MapperWorkflowNode(data, hydration);
  }
  override deriveTypedOutputShape(
    context: WorkflowNodeOutputShapeContext
  ): string | null {
    return (
      deriveMapperOutputShape({
        nodeId: this.id,
        data: this._data.data,
        context,
      }) ??
      context.resolvePredecessorOutputShape?.(this.id) ??
      null
    );
  }

  override upstreamRefs(): readonly WorkflowNodeUpstreamRef[] {
    return this.bindingMapRefs(this.config?.fieldBindings);
  }
}
